/**
 * The native and desktop shells' "email me a sign-in code" panel.
 *
 * Presentational. It owns the two input values and nothing else - which step is
 * showing, what went wrong, and every session consequence belong to
 * `use-email-code-sign-in.ts`, so this panel can be driven from a test without
 * a Supabase client anywhere near it.
 *
 * Both shells dress from entry tokens: they draw on the cream canvas, which is the same
 * colour in both themes, while the web auth chrome carries theme-flipping
 * semantic tokens (`@/components/brand/entry-styles`).
 *
 * It replaces the method stack while open rather than expanding beneath it. A 6-digit
 * entry sitting under three pills and a password form is a screen with two
 * competing "sign in" affordances and no obvious focus.
 */

import { Loading01 } from '@untitled-ui/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MFACodeInput } from '@/components/auth/mfa/MFACodeInput';
import { MfaCheckFailedNotice } from '@/components/auth/mfa-check-failed-notice';
import { NativeInput } from '@/components/shared/native-input';
import {
  authBrandOptionClass,
  authInlineErrorClass,
  entryBackLinkClass,
  entryLabelClass,
  entryLinkClass,
} from '@/components/brand/entry-styles';
import { Button } from '@/components/base/buttons/button';

import type { ChangeEvent } from 'react';
import type { EmailCodeNotice, EmailCodeStep } from '@/components/auth/use-email-code-sign-in';

interface DesktopEmailCodePanelProps {
  readonly step: EmailCodeStep;
  readonly notice: EmailCodeNotice;
  /** The address a code was requested for, echoed back on the code step. */
  readonly pendingEmail: string;
  readonly isLoading: boolean;
  readonly onSendCode: (email: string) => void;
  readonly onVerifyCode: (code: string) => void;
  /** Re-runs a step-up check that could not answer. Costs no new code. */
  readonly onRetryStepUp: () => void;
  readonly onClearNotice: () => void;
  /** Back to the address step, keeping the panel open. */
  readonly onUseDifferentEmail: () => void;
  /** Out of the panel entirely, back to the method stack. */
  readonly onClose: () => void;
}

const CODE_LENGTH = 6;

/** The step-up cell chrome, matching the sheet's: this is the same paper. */
const CODE_CELL_CLASS =
  'h-13 w-11 border-entry-ink/15 bg-entry-canvas text-xl font-semibold text-entry-ink ring-entry-ink ring-offset-0 first:rounded-l-xl last:rounded-r-xl';

export function DesktopEmailCodePanel({
  step,
  notice,
  pendingEmail,
  isLoading,
  onSendCode,
  onVerifyCode,
  onRetryStepUp,
  onClearNotice,
  onUseDifferentEmail,
  onClose,
}: DesktopEmailCodePanelProps) {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const emailRef = useRef<HTMLInputElement>(null);

  // Opening the panel and stepping back to it both land here, and both replace
  // the control the click came from - without this, focus falls to `<body>` and
  // a keyboard user has to tab in from the top of the document. The code step
  // needs no counterpart: `MFACodeInput` autofocuses itself.
  useEffect(() => {
    if (step === 'address') {
      emailRef.current?.focus();
    }
  }, [step]);

  // A rejected code has to leave the box, or the next attempt starts by
  // clearing six digits by hand. The notice returns to `none` at the start of
  // every verify, so a second failure is a fresh transition and trips this
  // again.
  //
  // Adjusted DURING RENDER rather than in an effect: this is a reset driven by
  // a prop change, which React resolves in this shape rather than with a
  // cascading second render (`react-hooks/set-state-in-effect`, and
  // react.dev/learn/you-might-not-need-an-effect).
  const [lastNotice, setLastNotice] = useState(notice);
  if (notice !== lastNotice) {
    setLastNotice(notice);
    if (notice === 'invalid_or_expired') {
      setCode('');
    }
  }

  const message =
    (notice === 'invalid_or_expired' && t('emailCode.invalidOrExpired')) ||
    (notice === 'rate_limited' && t('emailCode.rateLimited')) ||
    '';

  return (
    <div className="flex flex-col">
      <Button className={entryBackLinkClass} color="tertiary" size="md" onClick={onClose}>
        {t('emailCode.back')}
      </Button>

      <h1 className="text-display-xs leading-8 font-semibold tracking-normal text-entry-ink">
        {step === 'address' ? t('emailCode.heading') : t('emailCode.codeHeading')}
      </h1>

      <p className="mt-2 mb-6 text-md text-entry-ink/70">
        {step === 'address'
          ? t('emailCode.description')
          : t('emailCode.sentTo', { email: pendingEmail })}
      </p>

      {/* Mounted on both steps and never `display: none`. A live region
          introduced at the same time as its content is not reliably announced,
          and `not-empty:mb-4` keeps the empty node out of the layout. */}
      <p aria-live="polite" className={authInlineErrorClass} data-testid="email-code-notice">
        {message}
      </p>

      <MfaCheckFailedNotice
        className="not-empty:mb-4 text-sm text-entry-ink/70"
        show={notice === 'check_failed'}
        onRetry={onRetryStepUp}
      />

      {step === 'address' ? (
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            onSendCode(email);
          }}
        >
          <div className="flex flex-col gap-1.5">
            <label className={entryLabelClass} htmlFor="email-code-address">
              {t('emailCode.emailLabel')}
            </label>
            <NativeInput
              ref={emailRef}
              required
              autoComplete="email"
              disabled={isLoading}
              id="email-code-address"
              inputClassName="px-3.5 text-md text-entry-ink placeholder:text-entry-ink/35 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-entry-ink md:text-md"
              isDisabled={isLoading}
              placeholder={t('login.emailPlaceholder')}
              type="email"
              value={email}
              wrapperClassName="h-12 w-full rounded-xl border border-entry-ink/15 bg-entry-canvas shadow-none ring-0 focus-within:border-entry-ink/40 focus-within:ring-0"
              onChange={(event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
            />
          </div>

          <Button className={authBrandOptionClass} isDisabled={isLoading} size="md" type="submit">
            {isLoading ? (
              <>
                <Loading01 aria-hidden="true" className="mr-2 size-4 animate-spin" />
                {t('emailCode.sending')}
              </>
            ) : (
              t('emailCode.send')
            )}
          </Button>
        </form>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex justify-center">
            <MFACodeInput
              ariaLabel={t('emailCode.codeLabel')}
              disabled={isLoading}
              slotClassName={CODE_CELL_CLASS}
              value={code}
              onChange={(value) => {
                setCode(value);
                if (notice !== 'none') {
                  onClearNotice();
                }
              }}
              onComplete={onVerifyCode}
            />
          </div>

          <Button
            className={authBrandOptionClass}
            isDisabled={isLoading || code.length !== CODE_LENGTH}
            size="md"
            onClick={() => onVerifyCode(code)}
          >
            {isLoading ? (
              <>
                <Loading01 aria-hidden="true" className="mr-2 size-4 animate-spin" />
                {t('emailCode.verifying')}
              </>
            ) : (
              t('emailCode.verify')
            )}
          </Button>

          <div className="flex items-center justify-center gap-4">
            <Button
              className={entryLinkClass}
              color="link-color"
              isDisabled={isLoading}
              size="md"
              onClick={() => onSendCode(pendingEmail)}
            >
              {t('emailCode.resend')}
            </Button>
            <Button
              className={entryLinkClass}
              color="link-color"
              isDisabled={isLoading}
              size="md"
              onClick={onUseDifferentEmail}
            >
              {t('emailCode.useDifferentEmail')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
